/**
 * Search API Service
 * Handles TypeScript API (ts-api-container) requests for:
 * - Load searching
 * - Multi-city searches  
 * - User defaults
 * - Search logging
 * - Email alerts
 */
import BaseApiService from './BaseApiService';
import { getApiUrl } from '../../config/environment';

class SearchApiService extends BaseApiService {
  constructor() {
    super('SearchAPI', getApiUrl('search'));
  }
  
  /**
   * Test connectivity using root endpoint
   */
  async testConnectivity() {
    // Use root endpoint for simple connectivity test
    return super.testConnectivity('/', 5000);
  }
  
  /**
   * Search for loads
   * @param {Object} searchFilters - Search parameters
   * @returns {Promise<Object>} - Standardized API response with load data
   */
  async searchLoads(searchFilters) {
    try {
      console.log(`[${this.serviceName}] Searching loads with filters:`, searchFilters);
      
      const response = await this.get('/search-loads/', searchFilters);
      
      console.log(`[${this.serviceName}] Search completed - Found ${response.data?.length || 0} loads`);
      return response;
      
    } catch (error) {
      console.error(`[${this.serviceName}] Load search failed:`, error);
      throw error;
    }
  }
  
  /**
   * Multi-city search
   * @param {Object} searchFilters - Multi-city search parameters
   * @returns {Promise<Object>} - Standardized API response with load data
   */
  async searchMultiCity(searchFilters) {
    try {
      console.log(`[${this.serviceName}] Multi-city search with filters:`, searchFilters);
      
      const response = await this.get('/search-multi-trip/', searchFilters);
      
      console.log(`[${this.serviceName}] Multi-city search completed - Found ${response.data?.length || 0} loads`);
      return response;
      
    } catch (error) {
      console.error(`[${this.serviceName}] Multi-city search failed:`, error);
      throw error;
    }
  }
  
  /**
   * Update user defaults
   * @param {Object} defaults - User default settings
   * @returns {Promise<Object>} - Standardized API response
   */
  async updateUserDefaults(defaults) {
    try {
      console.log(`[${this.serviceName}] Updating user defaults:`, defaults);
      
      const response = await this.put('/user/defaults/', defaults);
      
      console.log(`[${this.serviceName}] User defaults updated successfully`);
      return response;
      
    } catch (error) {
      console.error(`[${this.serviceName}] Update user defaults failed:`, error);
      throw error;
    }
  }
  
  /**
   * Add search log entry
   * @param {Object} logData - Search log data
   * @returns {Promise<Object>} - Standardized API response
   */
  async addSearchLog(logData) {
    try {
      console.log(`[${this.serviceName}] Adding search log:`, logData);
      
      const response = await this.post('/user/search-log/', logData);
      
      console.log(`[${this.serviceName}] Search log added successfully`);
      return response;
      
    } catch (error) {
      console.error(`[${this.serviceName}] Add search log failed:`, error);
      throw error;
    }
  }
  
  /**
   * Save email alert
   * @param {Object} alertData - Email alert data
   * @returns {Promise<Object>} - Standardized API response
   */
  async saveEmailAlert(alertData) {
    try {
      console.log(`[${this.serviceName}] Saving email alert:`, alertData);
      
      const response = await this.post('/user/email-alert/', alertData);
      
      console.log(`[${this.serviceName}] Email alert saved successfully`);
      return response;
      
    } catch (error) {
      console.error(`[${this.serviceName}] Save email alert failed:`, error);
      throw error;
    }
  }
  
  /**
   * Get user profile data
   * @returns {Promise<Object>} - Standardized API response with user data
   */
  async getUserProfile() {
    try {
      console.log(`[${this.serviceName}] Getting user profile`);
      
      const response = await this.get('/user/profile/');
      
      console.log(`[${this.serviceName}] User profile retrieved successfully`);
      return response;
      
    } catch (error) {
      console.error(`[${this.serviceName}] Get user profile failed:`, error);
      throw error;
    }
  }
  
  /**
   * Update user role (Admin only)
   * @param {number} userId - User ID to update
   * @param {string} newRole - New role to assign
   * @returns {Promise<Object>} - Standardized API response
   */
  async updateUserRole(userId, newRole) {
    try {
      console.log(`[${this.serviceName}] Updating user role: User ${userId} -> ${newRole}`);
      
      const payload = {
        user_id_int: userId,
        user_role: newRole
      };
      
      const response = await this.put('/update_user_role', payload);
      
      console.log(`[${this.serviceName}] User role updated successfully`);
      return response;
      
    } catch (error) {
      console.error(`[${this.serviceName}] Update user role failed:`, error);
      throw error;
    }
  }
  
  /**
   * Get user list (Admin only)
   * @param {Array} testAccounts - Array of test account IDs to exclude
   * @returns {Promise<Object>} - Standardized API response with user list
   */
  async getUserList(testAccounts = [0]) {
    try {
      console.log(`[${this.serviceName}] Getting user list, excluding test accounts:`, testAccounts);
      
      const params = {
        test_accounts: testAccounts
      };
      
      const response = await this.get('/users_list', params);
      
      console.log(`[${this.serviceName}] User list retrieved - ${response.data?.length || 0} users`);
      return response;
      
    } catch (error) {
      console.error(`[${this.serviceName}] Get user list failed:`, error);
      throw error;
    }
  }
}

// Create and export singleton instance
export default new SearchApiService();